'use client';

/* ============================================================================
 * FeaturesV2 - the /features/ page.
 * ============================================================================
 * Thin content wrapper around the shared ProductPageV2 template (adapted
 * from the prostay.com v2 design system). All customer-facing copy lives in
 * messages/<locale>.json under Pages.Features; this file wires translations
 * to structural data (hrefs, scenes, integration names).
 * ========================================================================= */

import React from 'react';
import {useTranslations} from 'next-intl';
import ProductPageV2 from './products/ProductPageV2';
import InstantSiteScene from '@/components/v2/InstantSiteScene';
import BookingEngineScene from '@/components/v2/BookingEngineScene';

const NS = 'Pages.Features';

const INTEGRATIONS = [
  {name: 'Prostay Booking Engine', category: 'Bookings'},
  {name: 'Prostay PMS',            category: 'Live data'},
  {name: 'Google Analytics 4',     category: 'Analytics'},
  {name: 'Meta Pixel',             category: 'Analytics'},
  {name: 'Google Fonts',           category: 'Design'},
  {name: 'Custom domains',         category: 'DNS / HTTPS'}
];

export default function FeaturesV2() {
  const t = useTranslations(NS);
  const content = {
    meta: {eyebrow: t('meta.eyebrow'), name: t('meta.name')},
    hero: {
      headline: t('hero.headline'),
      lead: t('hero.lead'),
      primaryCta: {label: t('hero.primaryCta.label'), href: '/request-demo/'},
      secondaryCta: {label: t('hero.secondaryCta.label'), href: '/pricing/'},
      /* The deliverable is a public website; the "Runs on" pills describe
       * admin apps and would mislead here. */
      platforms: false,
      scene: <InstantSiteScene />,
      stats: t.raw('hero.stats')
    },
    capabilities: {
      headline: t('capabilities.headline'),
      lead: t('capabilities.lead'),
      items: t.raw('capabilities.items')
    },
    deepDives: [
      {
        eyebrow: t('deepDives.0.eyebrow'),
        headline: t('deepDives.0.headline'),
        body: t('deepDives.0.body'),
        bulletsLabel: t('deepDives.0.bulletsLabel'),
        bullets: t.raw('deepDives.0.bullets')
      },
      {
        eyebrow: t('deepDives.1.eyebrow'),
        headline: t('deepDives.1.headline'),
        body: t('deepDives.1.body'),
        scene: <BookingEngineScene />,
        bulletsLabel: t('deepDives.1.bulletsLabel'),
        bullets: t.raw('deepDives.1.bullets')
      },
      {
        eyebrow: t('deepDives.2.eyebrow'),
        headline: t('deepDives.2.headline'),
        body: t('deepDives.2.body'),
        bulletsLabel: t('deepDives.2.bulletsLabel'),
        bullets: t.raw('deepDives.2.bullets')
      },
      {
        eyebrow: t('deepDives.3.eyebrow'),
        headline: t('deepDives.3.headline'),
        body: t('deepDives.3.body'),
        bulletsLabel: t('deepDives.3.bulletsLabel'),
        bullets: t.raw('deepDives.3.bullets')
      }
    ],
    useCases: {
      headline: t('useCases.headline'),
      lead: t('useCases.lead'),
      items: t.raw('useCases.items')
    },
    integrations: {
      headline: t('integrations.headline'),
      lead: t('integrations.lead'),
      items: INTEGRATIONS
    },
    pricing: {
      headline: t('pricing.headline'),
      body: t('pricing.body'),
      ctaLabel: t('pricing.ctaLabel'),
      ctaHref: '/pricing/'
    },
    closing: {
      headline: t('closing.headline'),
      body: t('closing.body'),
      primaryCta: {label: t('closing.primaryCta.label'), href: '/request-demo/'},
      secondaryCta: {label: t('closing.secondaryCta.label'), href: '/templates/'},
      benefits: t.raw('closing.benefits')
    }
  };

  return <ProductPageV2 content={content} />;
}
